import { RouterStore } from 'mobx-react-router';
import { CurrentUserState } from '../../../../mainv2/states/CurrentUserState';
import { CreditCardState } from '../../../components/molecules/payment/states/CreditCardFormState';
import { ApplicationPage, ApplicationPageKey, BEAUTY_APPLICATION_PAGE_KEY } from '../../../types/applicationPages';
import { action, computed, observable } from 'mobx';
import { SubmitState } from '../../../../commonv3/states/SubmitState';
import { RemoteResourceV3, updateRemoteResourceV3 } from '../../../../commonv3/api/RemoteResourceV3';
import { ApplicationPageApis } from '../../../apis/applicationPages';
import { EnhancedLocationDescriptor, V3Routes } from '../../../routes';
import { UserFormState } from './UserFormState';
import { SheRavenClient } from '../../../../commonv2/modules/ravenClient';
import moment from 'moment-timezone';
import { CouponState } from './CouponState';
import { BaseListState } from '../../../../commonv3/states/LocationListState';
import { LocationId } from '../../../../commonv3/types/locations';
import { ServiceAgreementKey, ServiceAgreementState } from './../../../states/ServiceAgreementState';
import { ISODate } from '../../../../commonv3/types/dates';
import { GlobalStatesV3 } from '../../../../commonv3/states/GlobalStatesV3';
import { BaseApplicationPageState } from './BaseApplicationPageState';
import { SheEnv } from '../../../../commonv3/utils/envs';
import { UserAddressFormState } from './UserAddressFormState';

export class BeautyApplicationPageState implements BaseApplicationPageState {
    userState: CurrentUserState = null;
    routerState: RouterStore = null;
    cardState: CreditCardState = null;
    page_key: ApplicationPageKey = null;
    name = null;
    userFormState: UserFormState = new UserFormState();
    addressFormState: UserAddressFormState = new UserAddressFormState();
    baseListState: BaseListState = new BaseListState();
    serviceAgreementState: ServiceAgreementState = new ServiceAgreementState();
    @observable applicationPageResource: RemoteResourceV3<ApplicationPage> = RemoteResourceV3.loading();
    @observable couponState = new CouponState();
    @observable submitState = new SubmitState();
    @observable initState = new SubmitState();
    @observable startDate = null;
    @observable error = null;
    @observable startedFrom: EnhancedLocationDescriptor = null;
    @observable entranceInstallments = 1;
    @observable selectedLocationId = -1;

    constructor(userState: CurrentUserState, routerState: RouterStore) {
        this.userState = userState;
        this.routerState = routerState;
        this.cardState = new CreditCardState(userState);
    }

    async init() {
        this.page_key = BEAUTY_APPLICATION_PAGE_KEY;
        await this.initState.operate(async () => {
            return await Promise.all([
                this.reloadApplicationPage(),
                this.userState.reloadUser(),
                this.cardState.init(),
                this.baseListState.init(),
            ]);
        });

        if (this.applicationPageResource.isNotFound() || this.applicationPageResource.isOperationalError()) {
            this.routerState.replace('');
        } else if (this.applicationPage && !this.applicationPage.qualified) {
            this.routerState.replace('');
        } else {
            this.addressFormState.init();
            if (!this.userState.isSignedIn()) {
                this.userFormState.init();
                this.startedFrom = V3Routes.beautyApplicationUserInfo();
            } else {
                this.userFormState.init(this.userState.currentUser);
                this.startedFrom = V3Routes.beautyApplicationUserSummary();
            }
            this.serviceAgreementState.init(this.applicationPage.service_agreement_key || ServiceAgreementKey.STANDARD);
            this.routerState.replace(this.startedFrom);
        }
    }

    async reloadApplicationPage() {
        try {
            await updateRemoteResourceV3(
                this.applicationPageResource,
                (v) => (this.applicationPageResource = v),
                () => ApplicationPageApis.show(this.page_key, this.userEmail, this.couponState.appliedCouponCode)
            );
            this.name = this.applicationPage.name;
        } catch (error) {
            this.routerState.replace(V3Routes.home());
        }
    }

    @computed
    get applicationPage(): ApplicationPage {
        return this.applicationPageResource.get();
    }

    @computed
    get totalPrice() {
        const entranceFee = this.applicationPage.entrance_fee_item;
        let price = this.applicationPage.membership_item.total_price;
        if (entranceFee) {
            const option = entranceFee.options.find((o) => o.installments === this.entranceInstallments);
            price += option.monthly_payments[0];
        }
        return price;
    }

    @computed
    get canGoBack(): boolean {
        if (!this.startedFrom) {
            return false;
        }
        return this.startedFrom.pathname !== this.routerState.location.pathname;
    }

    @computed
    get userEmail() {
        if (this.userState.isSignedIn()) {
            return this.userState.currentUser.email;
        }
        return this.userFormState.fields.email.getDirtyValue();
    }

    @computed
    get membershipPlanId() {
        return this.applicationPage.membership_item.membership_plan_id;
    }

    @action.bound
    async setEntranceInstallments(installments: number) {
        this.entranceInstallments = installments;
    }

    @action.bound
    setStartDate(date) {
        this.startDate = moment(date);
    }

    @action.bound
    async validateStartDateAndNext() {
        if (!this.startDate) {
            return;
        }
        this.routerState.push(V3Routes.beautyApplicationSummary());
    }

    @action.bound
    async validateUserAndNext() {
        await this.submitState.operate(async () => {
            await this.userFormState.validate();
            if (this.userFormState.hasErrors()) {
                return;
            }
            this.routerState.push(V3Routes.beautyApplicationAddressInfo());
        });
    }

    @action.bound
    async validateUserAddressAndNext() {
        await this.addressFormState.validate();
        if (this.addressFormState.hasErrors()) {
            return;
        }
        if (this.hasSingleFixedEffectiveDate(this.applicationPage.fixed_effective_dates)) {
            this.setStartDate(this.applicationPage.fixed_effective_dates[0]);
            this.routerState.push(V3Routes.beautyApplicationSummary());
        } else {
            this.routerState.push(V3Routes.beautyApplicationStartDateSelect());
        }
    }

    hasSingleFixedEffectiveDate(fixed_effective_dates: Array<ISODate>): boolean {
        if (fixed_effective_dates.length == 1) {
            return true;
        } else {
            return false;
        }
    }

    @action.bound
    async validateCardAndNext() {
        await this.submitState.operate(async () => {
            if (this.cardState.editMode) {
                if (!(await this.cardState.validateNewCard())) {
                    return;
                }
            }
            this.error = null;
            this.routerState.push(V3Routes.beautyApplicationConfirmation());
        });
    }

    @action.bound
    async validateCouponCode() {
        await this.submitState.operate(async () => {
            await this.couponState.submit(this.userEmail, this.membershipPlanId);
            if (this.couponState.appliedCouponCode) {
                await Promise.all([this.reloadApplicationPage()]);
            }
        });
    }

    @action.bound
    selectHomeLocation(locationId: LocationId | null) {
        this.selectedLocationId = locationId;
    }

    @action.bound
    async validateFeeSummaryAndNext() {
        return GlobalStatesV3.routing.push(V3Routes.beautyApplicationPaymentMethod());
    }

    @action.bound
    async clearCoupons() {
        await this.submitState.operate(async () => {
            this.couponState.clearCoupons();
            await Promise.all([this.reloadApplicationPage()]);
        });
    }

    @computed
    get entranceFeeItemName(): string {
        return this.applicationPage.membership_item.price > 0 ? '入会金' : this.applicationPage.entrance_fee_item.name;
    }

    @computed
    get submitting(): boolean {
        return this.submitState.submitting;
    }

    @action.bound
    async submit() {
        try {
            await this.submitState.operate(async () => {
                this.error = null;
                const response = await ApplicationPageApis.apply(this.page_key, {
                    user: this.userState.isSignedIn() ? undefined : this.userFormState.getDirtyValue(),
                    user_address: this.addressFormState.getDirtyValue(),
                    card: this.cardState.newToken ? { token: this.cardState.newToken } : undefined,
                    entrance_installments: this.entranceInstallments,
                    coupon_code: this.couponState.appliedCouponCode,
                    effective_date: this.startDate.format('YYYY-MM-DD'),
                    home_location_id: this.selectedLocationId,
                });
                await this.userState.reloadUser();
                // Handle partial failures of orders
                if (response.orders.find((order) => order.payments.some((o) => o.status === 'error'))) {
                    this.error = response.orders;
                }
            });
            this.routerState.push(V3Routes.beautyApplicationComplete());
        } catch (e) {
            SheRavenClient.captureException(e, {
                user: this.userState.currentUser,
                applicationPage: this.applicationPage,
            });
            this.error = e;
        }
    }

    @action.bound
    complete() {
        location.href = this.serviceUrl();
    }

    @action.bound
    serviceUrl() {
        if (SheEnv.isDevelopment()) {
            return 'https://beauty.shelikes.jp/';
        }
        if (SheEnv.isStaging()) {
            return 'https://staging.beauty.shelikes.jp';
        }
        return 'https://localhost:4001';
    }

    @action.bound
    completeWithError() {
        location.href = V3Routes.payments().stringify();
    }

    @action.bound
    restart() {
        location.href = V3Routes.beautyApplication().stringify();
    }
}
